import React, { createContext, useContext, useEffect, useState } from "react"

export interface AgentPageContextValue {
  route: string
  entities: Record<string, unknown>
}

const ReadContext = createContext<AgentPageContextValue | null>(null)
const WriteContext = createContext<
  ((value: AgentPageContextValue | null) => void) | null
>(null)

/** Wrap the app once; pages opt in via {@link useRegisterAgentContext}. */
export const AgentPageContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [value, setValue] = useState<AgentPageContextValue | null>(null)
  return (
    <WriteContext.Provider value={setValue}>
      <ReadContext.Provider value={value}>{children}</ReadContext.Provider>
    </WriteContext.Provider>
  )
}

/** Register what the agent should know about the current page. Clears on unmount. */
export function useRegisterAgentContext(value: AgentPageContextValue): void {
  const setValue = useContext(WriteContext)
  const serialized = JSON.stringify(value)
  useEffect(() => {
    if (!setValue) return
    setValue(JSON.parse(serialized) as AgentPageContextValue)
    return () => setValue(null)
  }, [setValue, serialized])
}

export function useAgentPageContext(): AgentPageContextValue | null {
  return useContext(ReadContext)
}
